import { useState, type JSX } from 'react';

import { toPersianDigits } from '@bookora/shared';
import { Badge, Button, Dropdown, Grid, Icon, IconButton, type MenuProps } from '@bookora/ui';

import { SupportDialog } from '@/components/support-contact';

import { tourAnchor } from './anchors';
import { useHelpMenuItems, useHelpMenuStuck } from './help-menu-items';
import { useHubBadgeTotal } from './hub-sections';
import { openGuideHub } from './runtime';
import { COPY } from './tours/copy';
import { Announcement } from './welcome-dialog';

function helpLabel(badge: number): string {
  return badge > 0 ? COPY.help.triggerWithBadge(toPersianDigits(badge)) : COPY.help.trigger;
}

/**
 * The header's «راهنما» menu (≥ md) — the one Help entry. A labelled button
 * from `lg` up (no tooltip needed), an icon button between `md` and `lg`.
 * Carries `tourAnchor('shell.help')`; the veteran announcement renders as its
 * next sibling. A dot (and «راهنما، n پیشنهاد جدید») when a hub section,
 * the smart guide's «پیشنهادها», has something new — and the menu's first
 * item then says where they are.
 */
export function HelpMenu(): JSX.Element | null {
  const screens = Grid.useBreakpoint();
  const labelled = screens.lg !== false;
  const items = useHelpMenuItems();
  const stuck = useHelpMenuStuck();
  const badge = useHubBadgeTotal();
  const [supportOpen, setSupportOpen] = useState(false);

  const support = {
    key: 'guidance-support',
    icon: <Icon name="chat" />,
    label: COPY.help.support,
    onClick: () => setSupportOpen(true),
  };
  // Someone who seems stuck here (critic C6) gets support among the first
  // answers, not behind the divider.
  const pageAt = items.findIndex(
    (item) => item?.key === 'guidance-page' || item?.key === 'guidance-page-notes',
  );
  const menuItems: MenuProps['items'] = stuck
    ? [...items.slice(0, pageAt + 1), support, ...items.slice(pageAt + 1)]
    : [...items, { type: 'divider' }, support];

  const label = helpLabel(badge);
  return (
    <>
      <span className="bk-coachmark-anchor bk-help-entry">
        <Badge dot={badge > 0} offset={[-6, 6]}>
          <Dropdown trigger={['click']} placement="bottom-end" menu={{ items: menuItems }}>
            {labelled ? (
              <Button
                {...tourAnchor('shell.help')}
                type="text"
                icon={<Icon name="help" />}
                aria-haspopup="menu"
                aria-label={badge > 0 ? label : undefined}
              >
                {COPY.help.trigger}
              </Button>
            ) : (
              <IconButton
                {...tourAnchor('shell.help')}
                type="text"
                name="help"
                label={label}
                aria-haspopup="menu"
              />
            )}
          </Dropdown>
        </Badge>
        <Announcement placement="sibling" />
      </span>
      <SupportDialog open={supportOpen} onClose={() => setSupportOpen(false)} />
    </>
  );
}

/** The compact nav drawer's «راهنما و پیشنهادها» row. */
export function HelpNavRow({ onNavigate }: { onNavigate?: () => void }): JSX.Element | null {
  const badge = useHubBadgeTotal();
  return (
    <div className="bk-help-nav-row">
      <Button
        type="text"
        block
        icon={
          <Badge dot={badge > 0}>
            <Icon name="help" />
          </Badge>
        }
        aria-label={
          badge > 0 ? `${COPY.help.navRow}، ${toPersianDigits(badge)} پیشنهاد جدید` : undefined
        }
        onClick={() => {
          onNavigate?.();
          openGuideHub(undefined, 'help-menu');
        }}
      >
        {COPY.help.navRow}
      </Button>
    </div>
  );
}
